<template>
  <Teleport to="body">
    <Transition name="backdrop">
      <div v-if="pitcher" class="drawer-backdrop" @click="$emit('close')" />
    </Transition>
    <Transition name="drawer">
      <aside
        v-if="pitcher"
        class="drawer"
        role="complementary"
        :aria-label="`${pitcher.name} distributions`"
      >
        <header class="drawer__header">
          <div>
            <span class="drawer__role">{{
              pitcher.is_starter ? 'Starting Pitcher' : 'Relief Pitcher'
            }}</span>
            <h2 class="drawer__name">{{ pitcher.name }}</h2>
          </div>
          <button class="drawer__close" aria-label="Close" @click="$emit('close')">&#215;</button>
        </header>

        <div v-if="appearancePct !== null" class="drawer__appearance">
          <span class="appearance__bar" :style="{ width: `${appearancePct * 100}%` }" />
          <span class="appearance__label"
            >appeared in {{ pct(appearancePct) }} of simulated games</span
          >
        </div>

        <div class="drawer__charts">
          <div v-for="chart in charts" :key="chart.stat" class="drawer__chart">
            <div class="chart__header">
              <span class="chart__stat">{{ chart.stat }}</span>
              <span class="chart__mean">avg {{ fmtMean(chart.stat) }}</span>
            </div>
            <div v-if="chart.stat === 'IP'" class="chart__note">
              buckets represent whole-inning ranges (e.g. 1IP = 1.0&ndash;1.2)
            </div>
            <div v-if="chart.data.length === 0" class="chart__na">N/A</div>
            <template v-else>
              <div class="chart__tooltip">
                <span v-if="hovered?.startsWith(chart.stat + '-')" class="chart__tooltip-text">
                  {{ chart.labels[Number(hovered.split('-')[1])] }}:
                  {{ pct(chart.data[Number(hovered.split('-')[1])]) }}
                </span>
              </div>
              <div class="chart__bars">
                <div
                  v-for="(val, idx) in chart.data"
                  :key="idx"
                  class="bar-col"
                  @mouseenter="hovered = `${chart.stat}-${idx}`"
                  @mouseleave="hovered = null"
                >
                  <div
                    class="bar-fill"
                    :class="{ 'bar-fill--hot': hovered === `${chart.stat}-${idx}` }"
                    :style="{ height: `${val * 100}%` }"
                  />
                  <div class="bar-label">{{ chart.labels[idx] }}</div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </aside>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import type { PitcherProjectionResponse } from '../../api/games'

const props = defineProps<{
  pitcher: PitcherProjectionResponse | null
}>()

defineEmits<{ close: [] }>()

const hovered = ref<string | null>(null)
watch(
  () => props.pitcher,
  () => {
    hovered.value = null
  }
)

function normalize(buckets: number[], total: number): number[] {
  return total > 0 ? buckets.map(v => v / total) : buckets
}

function makeBuckets(
  data: number[],
  suffix: string,
  maxBuckets: number,
  appearanceTotal: number
): { data: number[]; labels: string[] } {
  if (!data || data.length === 0) return { data: [], labels: [] }
  const raw =
    data.length <= maxBuckets
      ? [...data]
      : [...data.slice(0, maxBuckets - 1), data.slice(maxBuckets - 1).reduce((s, v) => s + v, 0)]
  const labels = raw.map((_, i) =>
    i === raw.length - 1 && data.length > maxBuckets ? `${i}${suffix}+` : `${i}${suffix}`
  )
  return { data: normalize(raw, appearanceTotal), labels }
}

function makeIpBuckets(data: number[], maxInnings: number): { data: number[]; labels: string[] } {
  if (!data || data.length === 0) return { data: [], labels: [] }
  const inningBuckets: number[] = []
  for (let inn = 0; inn < maxInnings; inn++) {
    const base = inn * 3
    inningBuckets.push((data[base] ?? 0) + (data[base + 1] ?? 0) + (data[base + 2] ?? 0))
  }
  const tail = data.slice(maxInnings * 3).reduce((s, v) => s + v, 0)
  if (tail > 0) inningBuckets[maxInnings - 1] += tail
  const total = inningBuckets.reduce((s, v) => s + v, 0)
  const labels = inningBuckets.map((_, i) =>
    i === inningBuckets.length - 1 ? `${i}IP+` : `${i}IP`
  )
  return { data: normalize(inningBuckets, total), labels }
}

function pct(val: number): string {
  return `${(val * 100).toFixed(1)}%`
}

function fmtMean(stat: string): string {
  if (!props.pitcher) return '—'
  const p = props.pitcher
  const map: Record<string, number | undefined> = {
    IP: p.mean_ip,
    K: p.mean_k,
    BB: p.mean_bb,
    H: p.mean_h,
    ER: p.mean_er,
    HR: p.mean_hr
  }
  const v = map[stat]
  return v !== undefined ? v.toFixed(1) : '—'
}

const appearancePct = computed<number | null>(() => {
  const dist = props.pitcher?.ip_distribution
  if (!dist || dist.length === 0) return null
  const total = dist.reduce((s, v) => s + v, 0)
  if (total >= 0.999) return null
  return total
})

const charts = computed(() => {
  if (!props.pitcher) return []
  const ipDist = props.pitcher.ip_distribution
  const appearanceTotal = ipDist?.length ? ipDist.reduce((s, v) => s + v, 0) : 1
  return [
    { stat: 'IP', ...makeIpBuckets(ipDist, 9) },
    { stat: 'K', ...makeBuckets(props.pitcher.k_distribution, 'K', 11, appearanceTotal) },
    { stat: 'BB', ...makeBuckets(props.pitcher.bb_distribution, 'BB', 5, appearanceTotal) },
    { stat: 'H', ...makeBuckets(props.pitcher.h_distribution, 'H', 10, appearanceTotal) },
    { stat: 'ER', ...makeBuckets(props.pitcher.er_distribution, 'ER', 7, appearanceTotal) },
    { stat: 'HR', ...makeBuckets(props.pitcher.hr_distribution, 'HR', 3, appearanceTotal) }
  ]
})
</script>

<style>
@import '../../assets/drawer.css';
</style>

<style scoped>
/* Pitcher-specific extensions and overrides on top of shared drawer.css.
   Scoped so they don't leak to BatterDrawer (which is co-bundled by
   SimulateView and would otherwise inherit these). */
.drawer__appearance {
  padding: 0.75rem 1.75rem;
  border-bottom: var(--rule-thin) dashed var(--paper-edge);
  background: var(--paper-deep);
  flex-shrink: 0;
}

.appearance__label {
  display: block;
  font-family: var(--font-data);
  font-size: 0.65rem;
  color: var(--ink-faint);
  letter-spacing: 0.08em;
  margin-bottom: 0.5rem;
}

.appearance__bar {
  display: block;
  height: 3px;
  background: var(--ox);
  border-radius: 1px;
  max-width: 100%;
}

.chart__note {
  font-family: var(--font-data);
  font-size: 0.58rem;
  color: var(--ink-ghost);
  letter-spacing: 0.04em;
  font-style: italic;
  margin-top: -0.2rem;
}

.chart__bars {
  position: relative;
}

.bar-col {
  gap: 4px;
  position: relative;
}

.bar-label {
  text-overflow: clip;
}
</style>
